import React, { useContext, useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { DataContext } from '../../../init/getData';
import { Button, Table, Divider, Tag } from 'antd';
import { Col, Form, Row } from 'react-bootstrap';
import { dateTableFormatter } from '../../../dynamic/dateFormatter';
import { numberFormatter } from '../../../components/formatters/numberFormatter';
import { creditFormatter, debitFormatter } from '../../../components/formatters/defFormatter';
import { typeFormatter } from '../../../components/formatters/defFormatter';
import PrintViewSession from '../../../components/print/printViewSession';
import exportToExcel from '../../../components/export/excel'; 

const ViewSession = (props) => {
  const { api, userData, allUserData } = useContext(DataContext);
  const userId = userData.id;
  const userName = userData.name;

  const { showModal, toggleModal, idRow } = props;
  const [loadingEffect, setLoadingEffect] = useState(false);
  const [validated, setValidated] = useState(false);  
  const [showPrintComponent, setShowPrintComponent] = useState(false); 
  const [exportLoading, setExportLoading] = useState(false);


  const [detailedSessionData, setDetailedSessionData] = useState(null); 
  const [isLoading, setIsLoading] = useState(true); // Add a loading state

  useEffect(() => {
    // Fetch detailed data when the modal is shown and we have an ID
    if (showModal && idRow?.id) {
      const fetchDetailedSession = async () => {
        setIsLoading(true); // Start loading
        try {
          const response = await api.get(`session/getSessionById/${idRow.id}`);
          if (response.data.status) {
            setDetailedSessionData(response.data.data); // Set the detailed data
          } else {
            toast.error("Failed to load session details.");
          }
        } catch (error) {
          toast.error("Error fetching session details. Contact support.");
          console.error("Fetch error:", error);
        } finally {
          setIsLoading(false); // Stop loading
        }
      };

      fetchDetailedSession();
    }
  }, [showModal, idRow?.id, api]); // Dependency array
  


  // Ref for the printable content
  const printRef = useRef(); 

const handlePrintStatus = () => {
  setShowPrintComponent(true);
  setTimeout(() => {
    const printContent = printRef.current;
    if (printContent) {
      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=900,height=600');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Session Report</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 0;
                padding: 20px;
                background-color: white;
              }
              .print-container { 
                max-width: 100%; 
              }
              h2 { color: #000; margin-bottom: 15px; }
              h4 { 
                padding: 8px; 
                border-radius: 4px; 
                background-color: #f0f0f0; 
                margin-bottom: 10px;
              }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-bottom: 15px; 
                background-color: #fff; 
              }
              th, td { 
                border: 1px solid #000 !important; 
                padding: 8px; 
                text-align: left; 
              }
              th { 
                background-color: #f0f0f0; 
                font-weight: bold; 
              }
              tr { background-color: #fff !important; }
              .text-center { text-align: center; }
              .text-right { text-align: right; }
              .session-info div { margin-bottom: 5px; }
              .totals div { margin-bottom: 5px; font-weight: bold; }

              /* Add page margin fix for printing - Increased left and right margins */
              @media print {
                @page { 
                  margin: 20mm 30mm; /* 20mm top/bottom, 30mm left/right */
                }
                body { 
                  padding: 0; 
                  margin: 0; 
                }
                .print-container { 
                  width: 100%; 
                }
                /* Ensure content doesn't overflow with new margins */
                table {
                  width: 95% !important;
                  margin: 0 auto;
                }
                h2, h4 {
                  padding-left: 0px;
                  padding-right: 10px;
                }
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);

      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = function() {
        printWindow.focus();
        printWindow.print();
        // Don't close the window automatically - let user decide
      };
    }
    setShowPrintComponent(false);
  }, 100);
};

  // Rest of your component remains the same...
  // Function to get user name from createdBy ID
  const getUserName = (createdById) => {
    if (!createdById) return 'N/A';
    const user = allUserData.find(user => user.id === createdById);
    return user?.name || `User ${createdById}`;
  };

  // Columns for transactions table with credit/debit formatting
  const transactionColumns = [
    { 
      title: 'ID', 
      dataIndex: 'id', 
      width: '8%',
      sorter: (a, b) => a.id - b.id 
    },
    { 
      title: 'Type', 
      dataIndex: 'type', 
      width: '12%',
      render: typeFormatter
    },
    { title: 'Total In', dataIndex: 'total', width: '8%', render: debitFormatter },
    { title: 'Total Out', dataIndex: 'total', width: '8%', render: creditFormatter },
    { 
      title: 'Date', 
      dataIndex: 'createdAt', 
      width: '15%', 
      render: dateTableFormatter,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    },
    { 
      title: 'Account', 
      dataIndex: 'createdBy', 
      width: '15%', 
      render: getUserName   
    },
  ];



const prepareExcelData = () => {
  const sessionData = {
    'Session ID': detailedSessionData?.id,
    'User': detailedSessionData?.created?.name,
    'Opened At': dateTableFormatter(idRow?.openedAt),
    'Closed At': dateTableFormatter(idRow?.closedAt),
    'Balance': numberFormatter(idRow?.balance),
    'Status': idRow?.isClosed ? 'Closed' : 'Open',
    'Total Sales': numberFormatter(totalSales),
    'Total Returns': numberFormatter(totalReturns),
    'Net Total': numberFormatter(netTotal)
  };


  const transactionData = idRow?.transactions?.map(t => ({
    'ID': t.id,
    'Type': t.type,
    'Total In': debitFormatter(t.total, t),
    'Total Out': creditFormatter(t.total, t),
    'Date': dateTableFormatter(t.createdAt),
    'Account': t.created?.name || 'N/A',

  })) || [];

  return {
    transformedData: transactionData,
    summaryData: sessionData,
    title: `Session_${idRow?.id}_Report`,
  };
};

  // Function to handle Excel export
  const handleExportExcel = () => {
    setExportLoading(true);
    try {
      const excelData = prepareExcelData();
      if (!excelData.transformedData || excelData.transformedData.length === 0) {
        toast.error('No data available to export');
        return;
      }
      exportToExcel(excelData, excelData.title);
    } catch (error) {
      toast.error('Failed to export to Excel');
      console.error('Export error:', error);
    } finally {
      setExportLoading(false);
    }
  };

  // Calculate totals for display
  const calculateTransactionTotals = () => {
    if (!detailedSessionData?.transactions) return { totalSales: 0, totalReturns: 0, netTotal: 0 };
    
    
    const totalSales = detailedSessionData.transactions
      .filter(t => t.type === 'Sale' && !t.isOut)
      .reduce((sum, t) => sum + parseFloat(t.total || 0), 0);
    
    const totalReturns = detailedSessionData.transactions
      .filter(t => t.type === 'Return' && t.isOut)
      .reduce((sum, t) => sum + parseFloat(t.total || 0), 0);
    
    const netTotal = totalSales - totalReturns;
    
    return { totalSales, totalReturns, netTotal };
  };

  const { totalSales, totalReturns, netTotal } = calculateTransactionTotals();

return (
    <Modal show={showModal} backdrop="static" size={'xl'} keyboard={false} onHide={toggleModal}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); } }} >
      <Modal.Header style={{ display: 'flex', justifyContent: 'center', background: "#23576a" }}>
        <Modal.Title style={{ color: "white" }} >View Session - {detailedSessionData?.created?.name}</Modal.Title>
      </Modal.Header> 
      <Modal.Body>
        {isLoading ? (
          // Display a simple loading message while fetching data
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading session details...</p>
          </div>
        ) : (
          // Render the content once data is loaded
          <>
            {/* Session Summary */}
            <Row className="mb-4">
              <Col md={6}>
                <Row>
                  <Col md={6}><span style={{ fontWeight: 'bold' }}>Opened At:</span></Col>
                  <Col md={6}>{dateTableFormatter(detailedSessionData?.openedAt)}</Col>
                </Row>
                
                <Row>
                  <Col md={6}><span style={{ fontWeight: 'bold' }}>Closed At:</span></Col>
                  <Col md={6}>{dateTableFormatter(detailedSessionData?.closedAt || 'Still open')}</Col>
                </Row>
                
                <Row>
                  <Col md={6}><span style={{ fontWeight: 'bold' }}>Status:</span></Col>
                  <Col md={6}>
                    <Tag color={detailedSessionData?.isClosed ? 'green' : 'blue'}>
                      {detailedSessionData?.isClosed ? 'Closed' : 'Open'}
                    </Tag> 
                  </Col>
                </Row>
              </Col>
            </Row>

            <Divider />

            {/* Transaction Summary */}
            <Row className="mb-3">
              <Col md={4}>
                <Tag color="green" style={{ fontSize: '14px', padding: '8px' }}>
                  Total Sales: {numberFormatter(totalSales)}
                </Tag>
              </Col>
              <Col md={4}>
                <Tag color="red" style={{ fontSize: '14px', padding: '8px' }}>
                  Total Returns: {numberFormatter(totalReturns)}
                </Tag>
              </Col>
              <Col md={4}>
                <Tag color="blue" style={{ fontSize: '14px', padding: '8px' }}>
                  Net Total: {numberFormatter(netTotal)}
                </Tag>
              </Col>
            </Row>

            {/* Transactions table */}
            <Row className="mb-4">
              <div style={{ marginBottom: '10px' }}>
                <h5>Transactions ({detailedSessionData?.transactions?.length || 0})</h5>
              </div>
              <Table 
                columns={transactionColumns} 
                dataSource={detailedSessionData?.transactions} 
                pagination={{ pageSize: 10 }}
                scroll={{ y: 400 }}
                rowKey="id"
                summary={() => (
                  <Table.Summary>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={3} colSpan={4}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Row>

            <div className="modal-footer" style={{ justifyContent: 'center', marginTop: '20px', float: 'right' }}>
              <Button type="primary" size="large" onClick={toggleModal} style={{ minWidth: '100px', backgroundColor: '#d01818ff' }}>
                Close
              </Button>
              <Button type="default" size="large" onClick={handlePrintStatus} style={{ minWidth: '100px' }}>
                Print
              </Button>
              <Button 
                type="primary" 
                size="large" 
                onClick={handleExportExcel}
                loading={exportLoading}
                style={{ minWidth: '120px', backgroundColor: '#1890ff' }}
              >
                Export Excel
              </Button>
            </div>
          </>
        )}
      </Modal.Body>

      {showPrintComponent && (
        <div style={{ display: 'none' }}>
          <PrintViewSession
            ref={printRef}
            sessionData={detailedSessionData}
          />
        </div>
      )}
    </Modal>
  )
}

export default ViewSession;